import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Icon,
  Flex,
  SimpleGrid,
  Image,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import {
  FaGraduationCap,
  FaWhatsapp,
  FaChevronLeft,
  FaClock,
  FaImage
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import axiosInstance from '../services/axiosInstance';
import CobraSpinner from '../shared/CobraSpinner';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const PacksPage = () => {
  const [activeNiveau, setActiveNiveau] = useState(null);
  const [niveaux, setNiveaux] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Theme Constants
  const brandYellow = '#F2C919';
  const lightBg = 'gray.50';
  const primaryText = '#2C3E50';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [niveauxRes, packsRes] = await Promise.all([
          axiosInstance.get('/niveaux'),
          axiosInstance.get('/packs?activeOnly=true')
        ]);
        
        const fetchedNiveaux = niveauxRes.data.data || niveauxRes.data;
        setNiveaux(fetchedNiveaux);
        
        if (fetchedNiveaux && fetchedNiveaux.length > 0) {
          setActiveNiveau(fetchedNiveaux[0]._id);
        }

        const fetchedPacks = packsRes.data.packs || packsRes.data.data || packsRes.data;
        setPacks(Array.isArray(fetchedPacks) ? fetchedPacks : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const NiveauButton = ({ niveau, label, isActive, onClick }) => (
    <Button
      onClick={onClick}
      position="relative"
      size="lg"
      variant="unstyled"
      px={8}
      py={6}
      h="auto"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color={isActive ? 'black' : 'gray.500'}
      fontFamily="Tajawal, sans-serif"
      fontWeight="800"
      transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        color: isActive ? 'black' : 'gray.800',
        transform: 'translateY(-2px)'
      }}
    >
      {isActive && (
        <MotionBox
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg={brandYellow}
          borderRadius="full"
          zIndex={-1}
          layoutId="activeNiveauBg"
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          boxShadow={`0 10px 20px ${brandYellow}40`}
        />
      )}
      {!isActive && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="white"
          borderRadius="full"
          zIndex={-1}
          border="1px solid"
          borderColor="gray.200"
          boxShadow="sm"
        />
      )}
      <HStack spacing={3}>
        <Icon as={FaGraduationCap} />
        <Text>{label}</Text>
      </HStack>
    </Button>
  );

  const PackPhotoCard = ({ pack, index }) => {
    const imageUrl = pack.imageUrl ? (pack.imageUrl.startsWith('http') ? pack.imageUrl : `${axiosInstance.defaults.baseURL.replace('/api', '')}${pack.imageUrl}`) : null;

    return (
      <MotionBox
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: index * 0.1, type: "spring", bounce: 0.4 }}
        whileHover={{ scale: 1.05, y: -10 }}
        onClick={() => navigate(`/pack/${pack._id}`)}
        cursor="pointer"
        h="100%"
        w="100%"
      >
        <Box
          bg="white"
          borderRadius="3xl"
          overflow="hidden"
          position="relative"
          border="2px solid"
          borderColor={pack.isPopular ? brandYellow : "transparent"}
          transition="all 0.4s"
          h="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="0 20px 40px rgba(0,0,0,0.08)"
          aspectRatio={4/5}
        >
          {imageUrl ? (
            <Image src={imageUrl} alt={pack.nameAr || pack.name} w="100%" h="100%" objectFit="cover" />
          ) : (
            <VStack spacing={4} p={6} textAlign="center" bg="gray.100" w="100%" h="100%" justify="center">
              <Icon as={FaImage} boxSize={16} color="gray.300" />
              <Heading size="md" color={primaryText} fontFamily="Tajawal, sans-serif">{pack.nameAr || pack.name}</Heading>
              <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">اضغط للتفاصيل</Text>
            </VStack>
          )}

          {pack.isPopular && (
            <Badge
              position="absolute"
              top={4}
              right={4}
              bg={brandYellow}
              color="black"
              px={4}
              py={1}
              borderRadius="full"
              fontWeight="900"
              fontSize="sm"
              fontFamily="Tajawal, sans-serif"
              boxShadow="lg"
            >
              الأكثر طلباً
            </Badge>
          )}
        </Box>
      </MotionBox>
    );
  };

  if (loading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl" bg={lightBg}>
        <VStack spacing={6}>
          <CobraSpinner size="xl" color={brandYellow} thickness="4px" />
          <Text color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="lg">
            جاري تهيئة العروض...
          </Text>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" bg={lightBg} minH="100vh" color={primaryText} overflowX="hidden">
      <Header />

      {/* Hero Section */}
      <Box pt={{ base: 32, md: 40 }} pb={{ base: 16, md: 24 }} position="relative" zIndex={1}>
        <Container maxW="1400px" textAlign="center">
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <VStack spacing={8}>
              <Badge
                bg={`${brandYellow}20`}
                color={primaryText}
                px={6}
                py={2}
                borderRadius="full"
                fontSize="md"
                fontFamily="Tajawal, sans-serif"
                fontWeight="800"
              >
                عروض السنة الدراسية 2024-2025
              </Badge>
              <Heading 
                fontSize={{ base: '5xl', md: '7xl' }} 
                fontFamily="Tajawal, sans-serif" 
                fontWeight="900"
                lineHeight="1.2"
                color={primaryText}
              >
                اختر الباقة المناسبة للتلميذ
              </Heading>
              <Text fontSize={{ base: 'lg', md: '2xl' }} maxW="3xl" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                باقات تعليمية شاملة ومصممة خصيصاً لتلبية احتياجات التلاميذ في كافة المستويات، مع مرافقة بيداغوجية مستمرة طوال السنة.
              </Text>
            </VStack>
          </MotionBox>
        </Container>
      </Box>

      <Container maxW="1400px" py={10} position="relative" zIndex={1}>
        {/* Niveau Selection */}
        <VStack spacing={10} mb={20}>
          <Box w="100%" overflowX="auto" py={4} css={{
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none'
          }}>
            <HStack spacing={4} justify={{ base: 'flex-start', lg: 'center' }} minW="max-content" px={4}>
              {niveaux.map((niv) => (
                <NiveauButton
                  key={niv._id}
                  niveau={niv._id}
                  label={niv.nameAr || niv.name}
                  isActive={activeNiveau === niv._id}
                  onClick={() => setActiveNiveau(niv._id)}
                />
              ))}
            </HStack>
          </Box>
        </VStack>

        {/* Packs Grid */}
        <AnimatePresence mode="wait">
          <MotionBox
            key={activeNiveau}
            initial={{ opacity: 0, filter: 'blur(10px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, filter: 'blur(10px)' }}
            transition={{ duration: 0.4 }}
          >
            <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={8} justifyItems="center">
              {packs
                .filter(pack => pack.niveau && (pack.niveau._id === activeNiveau || pack.niveau === activeNiveau))
                .map((pack, index) => (
                  <PackPhotoCard key={pack._id || pack.id} pack={pack} index={index} />
                ))}
            </SimpleGrid>
            {packs.filter(pack => pack.niveau && (pack.niveau._id === activeNiveau || pack.niveau === activeNiveau)).length === 0 && (
              <Flex direction="column" align="center" justify="center" py={32} bg="white" borderRadius="3xl" border="1px dashed" borderColor="gray.300" boxShadow="sm">
                <Icon as={FaClock} boxSize={20} color="gray.300" mb={6} />
                <Text fontSize="2xl" color="gray.500" fontFamily="Tajawal, sans-serif" fontWeight="800">
                  قريباً جـداً.. انتظروا بقية العروض
                </Text>
              </Flex>
            )}
          </MotionBox>
        </AnimatePresence>

        {/* FAQ Section */}
        <Box mt={40} mb={20}>
          <VStack spacing={16}>
            <VStack spacing={6} textAlign="center">
              <Heading size="3xl" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="900">
                أسئلة شائعة
              </Heading>
              <Text fontSize="xl" color="gray.600" maxW="2xl" fontFamily="Tajawal, sans-serif" fontWeight="600">
                كل ما تريد معرفته عن التسجيل وكيفية الاستفادة من المحتوى التعليمي.
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%">
              {[
                {
                  question: "هل الباقة تشمل كامل السنة الدراسية؟",
                  answer: "نعم، بمجرد الاشتراك تحصل على وصول كامل لكافة الدروس والتمارين والحصص المباشرة من بداية السنة إلى الامتحانات النهائية."
                },
                {
                  question: "كيف يتم تفعيل الحساب بعد الدفع؟",
                  answer: "بعد إتمام عملية الدفع وإرسال الوصل عبر واتساب، يتم تفعيل حساب التلميذ في غضون ساعة واحدة وتزويده بكافة معلومات الدخول."
                },
                {
                  question: "هل يمكن لشخصين استخدام نفس الحساب؟",
                  answer: "الحساب شخصي وخاص بتلميذ واحد فقط لضمان دقة المتابعة البيداغوجية والتقييم، ولا يسمح بمشاركته."
                },
                {
                  question: "هل تقدمون ضماناً للنجاح؟",
                  answer: "نحن نوفر أفضل الأدوات والأساتذة لضمان التميز، والتزام التلميذ بمتابعة الدروس وانجاز التمارين هو المفتاح الحقيقي للنجاح."
                }
              ].map((faq, index) => (
                <Box 
                  key={index} 
                  p={8} 
                  bg="white" 
                  borderRadius="2xl" 
                  border="1px solid" 
                  borderColor="gray.100"
                  boxShadow="md"
                  transition="all 0.3s"
                  _hover={{ borderColor: brandYellow, transform: 'translateY(-5px)', boxShadow: 'xl' }}
                >
                  <VStack align="flex-start" spacing={4}>
                    <HStack spacing={4}>
                      <Box p={2} bg={`${brandYellow}20`} borderRadius="lg">
                        <Icon as={FaChevronLeft} color={brandYellow} />
                      </Box>
                      <Heading size="md" color={primaryText} fontFamily="Tajawal, sans-serif" fontWeight="800">
                        {faq.question}
                      </Heading>
                    </HStack>
                    <Text fontSize="md" color="gray.600" fontFamily="Tajawal, sans-serif" lineHeight="1.8" pl={12}>
                      {faq.answer}
                    </Text>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Box>

        {/* Final CTA Strip */}
        <Box
          mt={32}
          mb={20}
          position="relative"
          borderRadius="3xl"
          overflow="hidden"
          boxShadow="2xl"
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bgGradient={`linear(to-r, ${brandYellow}, #FF8C00)`}
            opacity={0.9}
            zIndex={0}
          />
          <Flex
            position="relative"
            zIndex={1}
            direction="column"
            align="center"
            justify="center"
            p={{ base: 12, md: 24 }}
            textAlign="center"
          >
            <VStack spacing={8}>
              <Heading size="2xl" color="black" fontFamily="Tajawal, sans-serif" fontWeight="900">
                لا تضيع فرصة التميز الدراسي
              </Heading>
              <Text fontSize="xl" color="blackAlpha.800" maxW="3xl" fontFamily="Tajawal, sans-serif" fontWeight="800">
                سجل الآن وانطلق في رحلة النجاح مع أفضل منصة تعليمية للتلاميذ في تونس.
              </Text>
              <Button
                size="xl"
                h="70px"
                px={12}
                bg="black"
                color="white"
                _hover={{ bg: 'white', color: 'black', transform: 'scale(1.05)' }}
                fontFamily="Tajawal, sans-serif"
                fontWeight="900"
                fontSize="lg"
                onClick={() => window.open('https://wa.me/21622779137', '_blank')}
                borderRadius="2xl"
                boxShadow="xl"
                leftIcon={<FaWhatsapp size={24} />}
                transition="all 0.3s"
                mt={4}
              >
                سجل تلميذك الآن
              </Button>
            </VStack>
          </Flex>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default PacksPage;
